import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

import type { Paged } from './records-ops.service';

export type CampaignChannel = 'SMS' | 'EMAIL';

export interface CampaignProcessor {
  code: string;
  label: string;
  channels: CampaignChannel[];
  required_columns: string[];
  optional_columns: string[];
  default_template_code: string | null;
}

export interface CampaignTemplateOption {
  id: string;
  name: string;
  code: string | null;
  channel: { value?: string; title?: string } | string;
}

export interface CampaignIssue {
  line: number;
  code: string;
  detail: string;
  name?: string;
  recipient?: string;
}

/** JSON execution report saved on the campaign (parse results, then send results). */
export interface CampaignReport {
  file: { name: string; sha256: string; size_bytes: number; processor: string; processor_version: number; header_row: number | null };
  period: { start: string | null; end: string | null } | null;
  parsed: {
    rows_parsed: number;
    with_recipient: number;
    skipped_no_recipient: number;
    invalid: number;
    valid: number;
    duplicates: number;
  };
  processed: {
    queued: number;
    sent: number;
    failed: number;
    delivered: number;
    by_provider?: Record<string, number>;
    by_country?: Record<string, number>;
    by_delivery_status?: Record<string, number>;
  };
  issues: CampaignIssue[];
  issues_truncated: number;
  warnings: { line: number; first_line: number; recipient: string }[];
  options: Record<string, unknown>;
  timing: { validated_at: string | null; started_at: string | null; finished_at: string | null; duration_s?: number };
}

export interface CampaignPreview {
  report: CampaignReport;
  samples: { line: number; recipient: string; subject: string; message: string }[];
}

export interface CampaignDetail {
  id: string;
  reference: string;
  name: string;
  channel: { value?: string; title?: string } | string;
  processor: string;
  status: { value?: string; title?: string } | string;
  original_filename: string;
  total_count: number;
  valid_count: number;
  invalid_count: number;
  skipped_count: number;
  queued_count: number;
  sent_count: number;
  failed_count: number;
  delivered_count: number;
  completion_percentage: number;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  execution_report: CampaignReport;
}

export interface CampaignProgress {
  id: string;
  reference: string;
  status: string;
  completion_percentage: number;
  counts: Record<string, number>;
  execution_report: CampaignReport;
}

export interface CampaignLine {
  id: string;
  line_index: number;
  recipient_display: string;
  variables: Record<string, unknown>;
  status: { value?: string; title?: string } | string;
  error: string;
  message_queue_id: string | null;
}

export interface CampaignUploadInput {
  file: File;
  name: string;
  channel: CampaignChannel;
  processor: string;
  template?: string;
  subject?: string;
  sender_name?: string;
  sender_email?: string;
  options?: Record<string, unknown>;
}

/** Back-office SMS/email campaigns driven by a spreadsheet (under ``auth/campaigns/``). */
@Injectable({ providedIn: 'root' })
export class CampaignService {
  private readonly api = inject(ApiService);

  processors(): Observable<CampaignProcessor[]> {
    return this.api.get<CampaignProcessor[]>('auth/campaigns/processors/');
  }

  templates(channel: CampaignChannel): Observable<Paged<CampaignTemplateOption>> {
    return this.api.get<Paged<CampaignTemplateOption>>('auth/campaign-templates/', {
      channel,
      is_active: 'true',
      page_size: 20,
    });
  }

  preview(input: CampaignUploadInput): Observable<CampaignPreview> {
    return this.api.post<CampaignPreview>('auth/campaigns/preview/', this.form(input));
  }

  upload(input: CampaignUploadInput): Observable<CampaignDetail> {
    return this.api.post<CampaignDetail>('auth/campaigns/upload/', this.form(input));
  }

  detail(id: string): Observable<CampaignDetail> {
    return this.api.get<CampaignDetail>(`auth/campaigns/${id}/`);
  }

  progress(id: string): Observable<CampaignProgress> {
    return this.api.get<CampaignProgress>(`auth/campaigns/${id}/progress/`);
  }

  lines(id: string, page = 1, lineStatus = ''): Observable<Paged<CampaignLine>> {
    const params: Record<string, string | number> = { page };
    if (lineStatus) {
      params['line_status'] = lineStatus;
    }
    return this.api.get<Paged<CampaignLine>>(`auth/campaigns/${id}/lines/`, params);
  }

  start(id: string): Observable<CampaignDetail> {
    return this.api.post<CampaignDetail>(`auth/campaigns/${id}/start/`, {});
  }

  cancel(id: string): Observable<CampaignDetail> {
    return this.api.post<CampaignDetail>(`auth/campaigns/${id}/cancel/`, {});
  }

  retry(id: string): Observable<CampaignDetail> {
    return this.api.post<CampaignDetail>(`auth/campaigns/${id}/retry/`, {});
  }

  result(id: string): Observable<Blob> {
    return this.api.getBlob(`auth/campaigns/${id}/result/`);
  }

  private form(input: CampaignUploadInput): Record<string, unknown> {
    const body: Record<string, unknown> = {
      file: input.file,
      name: input.name,
      channel: input.channel,
      processor: input.processor,
      subject: input.subject ?? '',
      sender_name: input.sender_name ?? '',
      sender_email: input.sender_email ?? '',
      options: JSON.stringify(input.options ?? {}),
    };
    if (input.template) {
      body['template'] = input.template;
    }
    return body;
  }
}
